import {
  CSSProperties,
  KeyboardEvent,
  useContext,
  useEffect,
  useState,
} from 'react'
import { FocusTrap } from 'focus-trap-react'

import { SanityMenuFeaturedLinkFragment } from '@data/sanity/queries/types/site'
import { isBrowser } from '@lib/helpers'
import { getActiveLinkCount } from '@lib/navigation'
import { SiteContext } from '@lib/site-context'

import MegaNavigationBackground from './mega-navigation-background'
import MegaNavigationBackdrop from './mega-navigation-backdrop'
import MegaNavigationDropdown from './mega-navigation-dropdown'

export interface CSSPropertiesWithHeight extends CSSProperties {
  '--h': number
  '--hpx': string
}

interface MegaNavigationProps {
  items: SanityMenuFeaturedLinkFragment[]
  headerHeight: number
}

const MegaNavigation = ({ items, headerHeight }: MegaNavigationProps) => {
  const { megaNavigation, toggleMegaNavigation } = useContext(SiteContext)

  const [hasFocus, setHasFocus] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [activeDropdownHeight, setActiveDropdownHeight] = useState<number>()

  const isOpen = megaNavigation.isOpen
  const backgroundHeight = isOpen
    ? headerHeight + (activeDropdownHeight ?? 0)
    : 0

  // Update dropdown open state when closing menu
  useEffect(() => {
    if (!isOpen && isDropdownOpen) {
      setIsDropdownOpen(false)
    }
  }, [isDropdownOpen, isOpen])

  useEffect(() => {
    if (!isLoaded && isOpen) {
      setIsLoaded(true)
    }

    if (isBrowser) {
      document.body.classList.toggle('overflow-hidden', isOpen)
    }
  }, [isLoaded, isOpen])

  const dropdowns = items.filter(
    (item) => item._type === 'navDropdown' && 'dropdownItems' in item,
  )

  if (dropdowns.length === 0 || !isLoaded) {
    return null
  }

  const handleKeyDown = ({ key }: KeyboardEvent<HTMLDivElement>) => {
    if (key === 'Escape') {
      toggleMegaNavigation(false)
    }
  }

  const activeLinkCount = getActiveLinkCount(
    dropdowns,
    isOpen,
    megaNavigation.activeId,
  )

  return (
    <>
      <FocusTrap
        active={isOpen && hasFocus && activeLinkCount > 0}
        focusTrapOptions={{ allowOutsideClick: true }}
      >
        <div
          className="hidden lg:block absolute top-full inset-x-0 z-30 bg-pageBG"
          onKeyDown={handleKeyDown}
          role="menu"
          tabIndex={-1}
        >
          {dropdowns.map((dropdown) => (
            <MegaNavigationDropdown
              key={dropdown._key}
              dropdown={dropdown}
              hasFocus={hasFocus}
              setHasFocus={setHasFocus}
              setActiveDropdownHeight={setActiveDropdownHeight}
            />
          ))}
        </div>
      </FocusTrap>

      <MegaNavigationBackground height={backgroundHeight} />
      <MegaNavigationBackdrop isOpen={isOpen} />
    </>
  )
}

export default MegaNavigation
